import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {ModalService} from "../../../../services/modal.service";
import {CreateCardDto} from "../../models/saved-card.models";
import {CardDataService} from "../../services/card-data.service";
import {filter, Subscription, tap} from "rxjs";
import {CardHolderNameService} from "../../../../services/card-holder-name.service";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {cardHolderNameValidator} from "../../../../validators/card-holder-name.validator";


interface SelectColorButton {
  id: number
  color: string
}
@Component({
  selector: 'app-add-new-card-dialog',
  templateUrl: './add-new-card-dialog.component.html',
  styleUrls: ['./add-new-card-dialog.component.scss']
})
export class AddNewCardDialogComponent implements OnInit, OnDestroy{

  @Output() cardCreated: EventEmitter<void> = new EventEmitter<void>()

  buttonsColor: SelectColorButton[] = [
    {id: 1, color: '#6ED1ED'},
    {id: 2, color: '#E89D53'},
    {id: 3, color: '#5BC59E'},
    {id: 4, color: '#7D58DD'},
    {id: 5, color: '#E89FE5'},
  ]

  selectedColor = ''

  isSending = false

  subs: Subscription[] = []

  form!: FormGroup

  constructor(
    private modalService: ModalService,
    private cardDataService: CardDataService,
  ) {
  }

  ngOnInit() {
    this.initSubscribe()
    this.initForm()
  }

  ngOnDestroy() {
    this.subs.forEach(item => item.unsubscribe())
  }

  initForm() {
    this.form = new FormGroup({
      cardNumber: new FormControl('', [Validators.required, Validators.minLength(16)]),
      mount: new FormControl('', [Validators.required, Validators.max(12), Validators.minLength(2)]),
      year: new FormControl('', [Validators.required,  Validators.minLength(2)]),
      cvv: new FormControl('', [Validators.required, Validators.minLength(3)]),
      cardHolderName: new FormControl('', [Validators.required, cardHolderNameValidator()]),
    })

    this.subs.push(
      this.form.valueChanges.subscribe({
        next: () => {
          if (!!this.form.get('mount')?.value && this.form.get('mount')?.value.length < 2 && this.form.get('mount')?.value > '1') {
            this.form.get('mount')?.patchValue(`0${this.form.get('mount')?.value}`)
          }
        }
      })
    )
  }

  get cardNumberFormatError() {
    return this.form.get('cardNumber')?.hasError('minlength')
  }

  get cardNumberRequiredError() {
    return this.form.get('cardNumber')?.hasError('required')
  }

  get mountFormatError() {
    return this.form.get('mount')?.hasError('max')
  }

  get mountMinError() {
    return this.form.get('mount')?.hasError('minlength')
  }

  get mountRequiredError() {
    return this.form.get('mount')?.hasError('required')
  }

  get yearFormatError() {
    return this.form.get('year')?.hasError('minlength')
  }

  get yearRequiredError() {
    return this.form.get('year')?.hasError('required')
  }

  get cvvFormatError() {
    return this.form.get('cvv')?.hasError('minlength')
  }

  get cvvRequiredError() {
    return this.form.get('cvv')?.hasError('required')
  }

  get cardHolderNameFormatError() {
    return this.form.get('cardHolderName')?.hasError('cardHolderNameNotValid')
  }

  get cardHolderNameRequiredError() {
    return this.form.get('cardHolderName')?.hasError('required')
  }

  initSubscribe() {
    this.subs.push(this.modalService.isModalOpen$.subscribe({
      next: (res) => {
        if (!res && this.form) {
          this.clearForm()
        }
      }
    }))
  }

  closeModal() {
    this.modalService.closeModal()
  }

  selectColor(color: string) {
    this.selectedColor = color
  }

  clearForm() {
    this.selectedColor = ''

    this.form.reset()
  }

  createCard() {
    this.isSending = true
    const cardData: CreateCardDto = {
      card_num: this.form.get('cardNumber')?.value,
      validity: `${this.form.get('mount')?.value}${this.form.get('year')?.value}`,
      cvc: this.form.get('cvv')?.value,
      full_name: this.form.get('cardHolderName')?.value,
      color: this.selectedColor
    }

    this.cardDataService.addCard(cardData).subscribe({
      next: async (res: any) => {
        if (res?.cardTokens?.length) {
          this.clearForm();
          this.cardCreated.emit();
          this.isSending = false;
          return;
        }

        // 2) Пришел ответ для 3DS v2 — открываем страницу для прохождения 3DS
        if (res?.secure3DVersion2?.is3DSVer2 === true && res?.orderId) {
          const popup = this.open3DSWindow({
            orderId: res.orderId,
            secure3DVersion2: res.secure3DVersion2
          });

          const payloadBase = {
            // Вся информация, которую бэк ждет для метода getAgentCardToken:
            pan: this.form.get('cardNumber')?.value,
            expDay: `${this.form.get('mount')?.value}${this.form.get('year')?.value}`,
            cvc: this.form.get('cvv')?.value,
            cardHolder: this.form.get('cardHolderName')?.value,
            color: this.selectedColor,
            payer_id: null,
          };

          try {
            const last4 = payloadBase.pan.slice(-4);
            await this.pollBindingAfter3DS(res.orderId, last4, payloadBase);
            popup?.close();

            this.clearForm();
            this.cardCreated.emit();
          } catch (e) {
            console.error(e);
          } finally {
            this.isSending = false;
          }
          return;
        }

        this.isSending = false;
      },
      error: () => { this.isSending = false; }
    });
  }

  private open3DSWindow(secureData: any) {
    const q = new URLSearchParams({ secureData: JSON.stringify(secureData) });
    // Страница для 3DS
    return window.open(`https://pay.qazaqpay.kz/3ds-ver2.html?${q.toString()}`,
      '_blank', 'width=420,height=680');
  }

  private pollBindingAfter3DS(orderId: string, last4: string, payloadBase: any) {
    return new Promise<void>((resolve, reject) => {
      const started = Date.now();
      const MAX_MS = 120_000;
      const STEP_MS = 1500;

      const tick = () => {
        const body = {
          card_num : (payloadBase.pan || '').replace(/\s/g, ''),
          validity : payloadBase.expDay,               // как в первом запросе
          cvc      : payloadBase.cvc,
          full_name: payloadBase.cardHolder,
          color    : payloadBase.color,
          // + добавляем признак финиш-шага 3DS
          threeDSRequest: { is3DSVer2: true, orderId }
        };
        this.cardDataService.addCard(body).subscribe({
          next: (res: any) => {
            if (res?.cardTokens?.length) return resolve();
            if (Date.now() - started > MAX_MS) return reject('binding timeout');
            setTimeout(tick, STEP_MS);
          },
          error: () => {
            if (Date.now() - started > MAX_MS) return reject('binding error');
            setTimeout(tick, STEP_MS);
          }
        });
      };
      tick();
    });
  }
}
